import {
  PageContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Loading,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VideoUploadDropzone } from '@/components/shared/VideoUploadDropzone';

interface HowToVideoResponse {
  success: boolean;
  data: {
    mediaId: string | null;
    media: { id: string; url?: string } | null;
  };
}

const QUERY_KEY = ['system-settings', 'how-to-video'] as const;

/**
 * Internal-only settings page for the global mobile "how-to" video. Uploads a
 * video (straight to S3) and stores it as a system-level setting served to every
 * hotel app; a hotel can override it with its own video on the hotel page.
 */
export function HowToVideoPage() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<HowToVideoResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get('/admin/system-settings/how-to-video'),
  });

  const setVideo = useMutation({
    mutationFn: (mediaId: string | null) =>
      apiClient.put('/admin/system-settings/how-to-video', { mediaId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const mediaId = data?.data.mediaId ?? undefined;

  const handleChange = (newMediaId: string | undefined) => {
    toast.promise(setVideo.mutateAsync(newMediaId ?? null), {
      loading: newMediaId ? 'Saving how-to video…' : 'Removing how-to video…',
      success: newMediaId ? 'How-to video updated' : 'How-to video removed',
      error: 'Failed to update how-to video',
    });
  };

  return (
    <PageContainer>
      <div className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">How-To Video</h1>
        <p className="text-muted-foreground mt-1">
          The default walkthrough video shown across all hotel apps.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Global how-to video</CardTitle>
          <CardDescription>
            Shown to guests in every hotel app (scan screen, hotel screen, and the
            Support section). A hotel can override it with its own video from the
            hotel page. The video is vertical — guests can play it fullscreen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loading />
            </div>
          ) : (
            <VideoUploadDropzone
              label="How-to video"
              value={mediaId}
              onChange={handleChange}
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
